import { Activity, ActivityType } from './activity.class';
import { AppUiRecordDoc } from '@omni/interfaces/app-ui/record.interface';
import {
  SalesOrderState,
  SalesOrderStatus,
} from '@omni/enums/order-capture/sales-order.enum';
import {
  FORMATTED,
  formattedText,
  text,
} from '@omni/utility/dynamics-row.util';

/**
 * An order on the account timeline in the new order flow: every order of the account, as the order
 * screen's History lists them. Typed as an order so the timeline's order
 * filter and icon apply; a tap opens it in the order screen.
 */
export class OrderCaptureActivity extends Activity {
  public orderNumber: string;
  public statusLabel: string;
  public totalLabel: string;
  public ownerName: string;
  public customerId: string;
  /** An order this device holds, shown offline: its deliveries, lines and total in one line. */
  public summary = '';
  /** From this device's record: the device decides how it opens, not its status. */
  public onDevice = false;

  constructor(raw: Record<string, unknown>) {
    super(null as unknown as object);
    this.ID = text(raw.salesorderid);
    this.type = ActivityType.Order;
    this.orderNumber = text(raw.ordernumber);
    this.subject = this.orderNumber;
    this.state = Number(raw.statecode);
    this.status = Number(raw.statuscode);
    this.statusLabel = formattedText(raw, 'statuscode');
    this.statusString = this.statusLabel;
    this.totalLabel = formattedText(raw, 'totalamount');
    this.ownerId = text(raw._ownerid_value);
    this.ownerName = formattedText(raw, '_ownerid_value');
    this.customerId = text(raw._customerid_value);
    // Dated by its last change, so a draft still being worked on stays near the top.
    const changed = dateOf(raw.modifiedon ?? raw.createdon);
    this.scheduledStart = changed;
    this.scheduledEnd = changed;
  }

  /** Offline, from this device's own record of the order; `statusLabel` is its save status. */
  public static fromDevice(
    record: AppUiRecordDoc,
    statusLabel: string,
    submitted: boolean,
  ): OrderCaptureActivity {
    const state = record.state as { orderId?: string } | null;
    const order = new OrderCaptureActivity({
      salesorderid: record.serverId ?? state?.orderId ?? record.localId,
      ordernumber: record.serverName ?? '',
      statecode: submitted ? SalesOrderState.Submitted : SalesOrderState.Active,
      // The timeline draws a device order's icon from its status.
      statuscode: submitted
        ? SalesOrderStatus.Submitted
        : SalesOrderStatus.Draft,
      [`statuscode${FORMATTED}`]: statusLabel,
      _customerid_value: record.parentId,
      createdon: record.createdOn,
      modifiedon: record.modifiedOn || record.createdOn,
    });
    order.summary = record.summary ?? '';
    order.onDevice = true;
    return order;
  }

  /** Still Active: an order its rep may carry on with. */
  public get isOpen(): boolean {
    return this.state === SalesOrderState.Active;
  }
}

function dateOf(value: unknown): Date {
  if (value instanceof Date) return value;
  const numeric = typeof value === 'string' && /^\d+$/.test(value);
  return new Date(numeric ? Number(value) : String(value ?? ''));
}
